package dev.kuml.portal.web.profile

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.suspendCancellableCoroutine
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.files.Blob
import org.w3c.files.File
import kotlin.coroutines.resume

private const val TARGET_SIZE = 512
private const val JPEG_QUALITY = 0.85

/**
 * Client-side re-encode of a user-selected image file BEFORE upload -- pure convenience/bandwidth,
 * NOT a security boundary. `dev.kuml.portal.server.users.AvatarValidator` on the server is the
 * only validation that matters and re-validates from scratch regardless of what this function
 * produces or whether it runs at all -- a browser without `toBlob`/`createImageBitmap` support (or
 * any failure along the way) simply returns `null`, and the caller (`ProfileScreen`) falls back to
 * uploading the original [file] unchanged; the server still accepts or rejects it on its own terms.
 *
 * Always encodes as `image/jpeg`, never `image/webp`: `canvas.toBlob("image/webp")` on a canvas
 * with an alpha channel emits a `VP8X`-container WebP the moment any pixel is non-opaque, and the
 * server deliberately rejects `VP8X` outright (see `AvatarValidator`'s KDoc, "WebP-Subformat").
 * Drawing onto an OPAQUE (white-filled) canvas and encoding as JPEG sidesteps that coupling
 * entirely and is supported in every evergreen browser. This also strips any EXIF metadata the
 * source file carried (the re-encode never copies it) -- again convenience, not the security
 * boundary: the server strips EXIF from any accepted JPEG regardless (see `AvatarValidator`'s
 * sanitization step).
 *
 * Crops to a centered square, then scales to [TARGET_SIZE]x[TARGET_SIZE] pixels ("cover" crop) --
 * an avatar is always displayed circular/square, so this avoids uploading portrait/landscape
 * padding nobody will ever see.
 */
public suspend fun encodeAvatar(file: File): Blob? {
    val bitmap =
        try {
            window.createImageBitmap(file).await()
        } catch (e: Throwable) {
            return null
        }

    val canvas = document.createElement("canvas") as HTMLCanvasElement
    canvas.width = TARGET_SIZE
    canvas.height = TARGET_SIZE
    val ctx = canvas.getContext("2d") as? CanvasRenderingContext2D ?: return null

    // Opaque background -- see this function's KDoc for why (keeps the JPEG encode alpha-free).
    ctx.fillStyle = "#ffffff"
    ctx.fillRect(0.0, 0.0, TARGET_SIZE.toDouble(), TARGET_SIZE.toDouble())

    val sourceWidth = bitmap.width.toDouble()
    val sourceHeight = bitmap.height.toDouble()
    val cropSize = minOf(sourceWidth, sourceHeight)
    val cropX = (sourceWidth - cropSize) / 2.0
    val cropY = (sourceHeight - cropSize) / 2.0

    ctx.drawImage(
        bitmap,
        cropX,
        cropY,
        cropSize,
        cropSize,
        0.0,
        0.0,
        TARGET_SIZE.toDouble(),
        TARGET_SIZE.toDouble(),
    )
    bitmap.close()

    return suspendCancellableCoroutine { cont ->
        try {
            canvas.toBlob({ blob -> cont.resume(blob) }, "image/jpeg", JPEG_QUALITY)
        } catch (e: Throwable) {
            cont.resume(null)
        }
    }
}
